/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2017, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 */
define([
	'./postProcess/LocalAggregateSortInfo',
	'../../dashboard/queryService/QuerySelectionBuilder',
	'underscore'
], function(
	LocalAggregateSortInfo,
	QuerySelectionBuilder,
	_
) {
	class VisQuerySelectionBuilder extends QuerySelectionBuilder{
		constructor({ internalVisualization, layerToQuerySlotsMap, applyServerSort }) {
			super();
			this.internalVisualization = internalVisualization;
			this._serverSort = applyServerSort;
			this._localAggregateSortInfo = new LocalAggregateSortInfo(internalVisualization);
			this._dataItemPriorities = this._flattenDataItemPriorities(layerToQuerySlotsMap);
		}

		_forEachQuerySlotDataItem(layerToQuerySlotsMap, callback) {
			layerToQuerySlotsMap.forEach((querySlots)=>{
				querySlots.forEach(slot=>{
					const dataItems = slot.getDataItemList();
					dataItems.forEach(item=>{
						callback(item, slot);
					});
				});
			});
		}

		/**
		 * override the QuerySelectionBuilder.build
		 */
		build(dataItem, slot, outputEdgeFilterExceptions) {
			const finalSelections = [];
			//By default, return selections in the order agreed to by PM.
			finalSelections.push(...this._buildSelectionsFromDrillStateForHierarchyOrSet(dataItem));
			finalSelections.push(...this._buildSelectionsFromLocalFiltersForOLAPDataItems(dataItem, outputEdgeFilterExceptions));
			finalSelections.push(...this._buildSelectionsFromSearchFiltersForOLAPDataItems(dataItem, outputEdgeFilterExceptions));
			finalSelections.push(...this._buildSelectionsForTopBottom(dataItem));
			finalSelections.push(...this._buildSelectionsForSort(dataItem, slot));

			return finalSelections;
		}

		//For hierarchies or OLAP set, build one of root members, children of single root or children of current drill member.
		_buildSelectionsFromDrillStateForHierarchyOrSet(dataItem) {
			const localFilters = this.internalVisualization.getLocalFilters();
			const filterList = localFilters && localFilters.getFilterList() || [];
			return super._buildSelectionsFromDrillStateForHierarchyOrSet(dataItem, filterList);
		}

		_buildSelectionsFromSearchFiltersForOLAPDataItems(dataItem, outputEdgeFilterExceptions) {
			const searchFilterList = this.internalVisualization.getSearchFilterList();
			return super._buildSelectionsFromFiltersForOLAPDataItems(dataItem, searchFilterList, outputEdgeFilterExceptions);
		}

		_buildSelectionsFromLocalFiltersForOLAPDataItems(dataItem, outputEdgeFilterExceptions) {
			const localFilters = this.internalVisualization.getLocalFilters();
			const localFilterList = localFilters.getFilterList();
			return super._buildSelectionsFromFiltersForOLAPDataItems(dataItem, localFilterList, outputEdgeFilterExceptions);
		}

		/*
		 * Sort application priority:
		 * Apply user defined sort if there is one,
	 	 * Apply model defined sort if there is one and user defined sort doesn't exit,
		 * Apply default sort
		 */
		_buildSelectionsForSort(dataItem, slot) {
			const bClientSortEnabled = this._serverSort ? false : this._isClientSideSortEnabledForDataItem(dataItem);

			let selections = [];
			if (!bClientSortEnabled) {
				const { sort } = this._getSort(slot, dataItem);

				if (sort) {

					const sortObj = {
						...sort,
						priority: this._dataItemPriorities[dataItem.getId()]
					};

					const sortSelectionSpec = {
						'operation': 'order',
						'sort': _.pick(sortObj, 'type', 'by', 'priority')
					};

					if (sortObj.context){
						sortSelectionSpec.context = [{
							itemId: sortObj.context
						}];
					}
					selections = [sortSelectionSpec];
				}
			}
			return selections;
		}

		_isClientSideSortEnabledForDataItem(dataItem) {
			const localSortInfo = this._localAggregateSortInfo.getSortInfo();
			if (localSortInfo) {
				return localSortInfo.aggregateDataItemId === dataItem.getId();
			}
			return false;
		}

		/*
		 * Determines the sort payload for a data item.
		 * @return {object} with two values:
		 *  1. `sort` - the actual sort payload
		 *  2. `tier` - a representation of the precedence of this sort. 
		 *              All sorts in a single tier will ultimately be assigned unique `priority`s, however
		 *              we are not necessarily ready to perform that operation when `_getSort` is called. 
		 *             `tier` represents a coarse grouping of sorts by their type. Sorts in higher tiers will
		 *             ultimately get higher `priority` than lower tiers.
		 */
		_getSort(slot, dataItem) {
			// Tier 0: user-defined sorts.
			// An explicit sort performed by a user wins over all other kinds of sorts
			const dataItemOrModelSort = dataItem.getSort();
			if(dataItemOrModelSort && dataItemOrModelSort.type) {
				if(!dataItem.hasDefaultSort() || dataItem.hasModelSort()) {
					return {
						tier: 0,
						sort: dataItemOrModelSort
					};
				}
			}

			// Tier 1: top/bottom sorts.
			// A sort is automatically applied when there's a top/bottom operation to put the top- or bottom-
			// most result first. It is overridden by user-defined sorts, but overrides other sorts.
			const topBottomSort = this._getTopBottomSort(dataItem);
			if(topBottomSort) {
				return {
					tier: 1,
					sort: topBottomSort
				};
			}

			// Tier 2: default slot sorts.
			// In the absence of other sorts, we use the default sort on the slot
			if (!this._ignoreDefaultSlotSort(dataItem)) {
				const defaultSort = this._getDefaultSort(slot, dataItem);
				if(defaultSort) {
					return {
						tier: 2,
						sort: defaultSort
					};
				}
			}

			// No available sort
			return {};
		}

		_getTopBottomSort(dataItem) {
			const topBottomInfo = dataItem.getTopBottom();
			if (topBottomInfo) {
				const topBottomType = topBottomInfo.type;
				const sortType = (topBottomType === 'topcount' || topBottomType === 'toppercent') ? 'desc' : 'asc';
				const sortObj = {
					type: sortType
				};
				if (topBottomInfo.context) {
					//Dataitem with top/bottom will always sort by 'value'.
					sortObj.by = 'value';
					sortObj.context = topBottomInfo.context.itemId;
				}
				return sortObj;
			}
		}

		/**
		 *
		 * @param {*} slot
		 * @param {*} dataItem
		 * todo: the original implementation at VisDataItemManager.getDefaultSort() need to be removed once useAPI.
		 */
		_getDefaultSort (slot, dataItem) {
			let sortObj = null;
			let sortType;
			// Sort from slot definition
			sortType = slot.getDefinition().getSort();
			// since after binning, dataItem type is attribute, if there is no sort on slot and measure add 'asc' sort
			if (!sortType && !!dataItem.getBinning()) {
				sortType = 'asc';
			}
			if (sortType){
				sortObj = {
					type: sortType
				};
			}
			return sortObj;
		}

		/*
		 * Determine if default sort should be used.
		 * Don't use when data source might have a custom sort we don't know about it. 
		 * (Default sort would override such a sort).
		 * @param {Object} dataItem - current dataItem; see {@link DataItemAPI}
		 * @returns {boolean} true if the default sort is to be ignored, false otherwise
		 *
		 * todo: the original implementation at VisDataSlotsManager.ignoreDefaultSlotSort() need to be removed once useAPI.
		 */
		_ignoreDefaultSlotSort(dataItem) {
			return dataItem.getMetadataColumn().isOlapColumn() || dataItem.getMetadataColumn().isNamedSet();
		}

		_flattenDataItemPriorities(layerToQuerySlotsMap) {

			// Collect all sort info
			const sortList = [];
			let i = 0;
			this._forEachQuerySlotDataItem(layerToQuerySlotsMap, (dataItem, slot) => {
				const { sort, tier } = this._getSort(slot, dataItem);
				if(sort) {
					sortList.push({ dataItem, sort, tier, index: i++ });
				}
			});

			// Find largest priority (i.e. lowest precedence)
			let maxPriority = 0;
			sortList.forEach(
				({ sort }) => {
					if(sort.priority && sort.priority > maxPriority) {
						maxPriority = sort.priority;
					}
				}
			);
			maxPriority++;

			// Order all sorts
			sortList.sort(
				(sortA, sortB) => {
					// Tier is the coarsest granularity of sort
					if(sortA.tier !== sortB.tier) {
						return sortA.tier - sortB.tier;
					}

					// For sorts in the same tier, honour their relative priority
					// (if priority not set, assign largest priority, i.e. lowest precedence)
					const priorityA = 'priority' in sortA.sort ? sortA.sort.priority : maxPriority;
					const priorityB = 'priority' in sortB.sort ? sortB.sort.priority : maxPriority;
					if(priorityA !== priorityB) {
						return priorityA - priorityB;
					}

					// For sorts in same tier, with same priority, honour order as they appear
					// originally in their slots
					return sortA.index - sortB.index;
				}
			);

			// Build data item id --> relative priority mapping
			const dataItemSortPriorities = {};
			sortList.forEach(
				({ dataItem }, index) => dataItemSortPriorities[dataItem.getId()] = index
			);
			return dataItemSortPriorities;
		}

	}
	return VisQuerySelectionBuilder;
});
